import React, { useState, useEffect, useRef } from 'react';
import AWS from 'aws-sdk';
import './BiofoulingMonitoringPage.css';
import { useNavigate } from 'react-router-dom';
import { format, differenceInDays } from 'date-fns';
import emailjs from 'emailjs-com';
import fireImage from './fire.png';

AWS.config.update({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
});
const s3 = new AWS.S3();

const BiofoulingMonitoringPage = () => {
  const [fleetImage, setFleetImage] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('Month');
  const [lastGeneratedTime, setLastGeneratedTime] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const audioRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchImage('idle_periods_month.png');

    if (lastGeneratedTime !== null) {
      checkLastGeneratedTime();
    }
  }, [lastGeneratedTime]);

  useEffect(() => {
    setLastGeneratedTime(new Date());
  }, []);

  const fetchImage = (key) => {
    const params = {
      Bucket: 'git-dash-bucket',
      Key: key,
    };

    s3.getObject(params, (err, data) => {
      if (err) {
        console.error('Error fetching image:', err);
        setError(`Error fetching fleet insights image (${key}).`);
      } else {
        const blob = new Blob([data.Body], { type: data.ContentType });
        const imageObjectURL = URL.createObjectURL(blob);
        setFleetImage(imageObjectURL);

        const lastModifiedDate = data.LastModified;
        const formattedDate = format(lastModifiedDate, 'do MMMM yyyy, hh:mm aa');
        setLastUpdated(formattedDate);

        const daysDifference = differenceInDays(new Date(), new Date(lastModifiedDate));
        if (daysDifference > 1) {
          //sendEmailNotification();
        }
      }
    });
  };

  const checkLastGeneratedTime = () => {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
  };

  const handleFireImageClick = () => {
    console.log("Fire image clicked.");
    navigate('/fire');
  };
  

  const handleButtonClick = (period) => {
    setSelectedPeriod(period);
    const imageKeyMap = {
      'Week': 'idle_periods_week.png',
      'Month': 'idle_periods_month.png',
      'Full': 'idle_periods_full.png',
    };

    const key = imageKeyMap[period];
    if (key) {
      fetchImage(key);
    }
  };

  const GraphLegend = () => {
    const [selectedPart, setSelectedPart] = useState(null);

    const handlePartHover = (part) => {
      setSelectedPart(part);
    };

    const handlePartLeave = () => {
      setSelectedPart(null);
    };

    return (
      <div className="graph-legend">
        <div className="vertical-bar1 with-arrow">
          <div className="bar-content">
            {`Houston\n\n4.63\n\n12°C\n\n31.9g/kg`}
          </div>
          <div
            className={`bar-section1 port ${selectedPart === 'port' ? 'selected' : ''}`}
            onMouseEnter={() => handlePartHover('port')}
            onMouseLeave={handlePartLeave}
          ></div>
          <div
            className={`bar-section1 days-idle ${selectedPart === 'days-idle' ? 'selected' : ''}`}
            onMouseEnter={() => handlePartHover('days-idle')}
            onMouseLeave={handlePartLeave}
          ></div>
          <div
            className={`bar-section1 temperature ${selectedPart === 'temperature' ? 'selected' : ''}`}
            onMouseEnter={() => handlePartHover('temperature')}
            onMouseLeave={handlePartLeave}
          ></div>
          <div
            className={`bar-section1 salinity ${selectedPart === 'salinity' ? 'selected' : ''}`}
            onMouseEnter={() => handlePartHover('salinity')}
            onMouseLeave={handlePartLeave}
          ></div>
        </div>

        <div className="space-between-bars"></div>

        <div className="vertical-bar">
          <div
            className={`bar-section port ${selectedPart === 'port' ? 'selected' : ''}`}
            onMouseEnter={() => handlePartHover('port')}
            onMouseLeave={handlePartLeave}
          ></div>
          <div
            className={`bar-section days-idle ${selectedPart === 'days-idle' ? 'selected' : ''}`}
            onMouseEnter={() => handlePartHover('days-idle')}
            onMouseLeave={handlePartLeave}
          ></div>
          <div
            className={`bar-section temperature ${selectedPart === 'temperature' ? 'selected' : ''}`}
            onMouseEnter={() => handlePartHover('temperature')}
            onMouseLeave={handlePartLeave}
          ></div>
          <div
            className={`bar-section salinity ${selectedPart === 'salinity' ? 'selected' : ''}`}
            onMouseEnter={() => handlePartHover('salinity')}
            onMouseLeave={handlePartLeave}
          ></div>
        </div>

        <div className="legend-labels">
          <div className="label">Port Name</div>
          <div className="label">Days Idle</div>
          <div className="label">Temperature (∘C)</div>
          <div className="label">Salinity (g/kg)</div>
        </div>
        <div className="legend-text">
          Order of things on the bar graph
        </div>
        {selectedPart && <div className="part-info">{selectedPart}</div>}
      </div>
    );
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const openImageInFullscreenNewTab = () => {
    if (fleetImage) {
      const newTab = window.open(fleetImage, '_blank');
      newTab.document.write(`<img src="${fleetImage}" style="width: 85vw; height: 110vh;" />`);
      newTab.document.write(`<a href="${fleetImage}" download="fleet_insights.png">Download</a>`);
      newTab.document.close();
    }
  };

  return (
    <div className={`biofouling-monitoring-container ${darkMode ? 'dark-mode' : ''}`}>
      <button className="toggle-dark-mode" onClick={toggleDarkMode}>
        <img src={require('./dark.png')} alt="dark mode" />
      </button>
      <div className="left-panel">
        <div className="fleet-insights-container">
          <div className="fleet-insights-title">Vessels at Most Risk</div>
          <div className="last-updated">Last Updated: {lastUpdated}</div>
          {fleetImage ? (
            <div>
              <img
                src={fleetImage}
                alt="Fleet Insights"
                className="fleet-insights-image"
                onClick={openImageInFullscreenNewTab}
              />
              <GraphLegend />
              <div className="wave"></div>
            </div>
          ) : (
            'Loading Fleet Insights...'
          )}
        </div>
        <div className="buttons-container">
          <button className="button" onClick={() => handleButtonClick('Week')}>Week</button>
          <button className="button" onClick={() => handleButtonClick('Month')}>Month</button>
          <button className="button" onClick={() => handleButtonClick('Full')}>Full</button>
        </div>
      </div>
      <div className="right-panel">
        <div className="vessel-list-title">Top 10 Most Idle Vessels</div>
        <VesselList period={selectedPeriod} />
        <div className="biofouling-card">
    <div className="biofouling-card-title">What is Biofouling?</div>
    <div className="biofouling-card-content">
      <p>Biofouling refers to the accumulation of aquatic organisms like algae, barnacles, and mollusks on the submerged parts of a vessel, 
        particularly the hull. These organisms attach themselves to the surface, forming layers of growth.</p>
      <p><strong>Why is it not good for vessels?</strong></p>
      <ul>
        <li><strong>Increased fuel consumption:</strong> The extra drag caused by the biofouling growth makes the vessel
         work harder to move, consuming more fuel.</li>
        <li><strong>Corrosion:</strong> Biofouling can lead to faster corrosion of the hull as the organisms 
        trap moisture against the surface.</li>
        <li><strong>Spread of invasive species:</strong> Biofouling can transport invasive aquatic species
         from one region to another, causing ecological damage.</li>
        <li><strong>Reduced speed:</strong> The weight and resistance of biofouling can reduce 
        the vessel’s speed and efficiency.</li>
      </ul>
    </div>
  </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      
      <div className="fire-button-container">
        <img 
          src={fireImage} 
          alt="Fire Button" 
          className="fire-button" 
          onClick={(e) => {
            e.stopPropagation();
            handleFireImageClick();
          }} 
          style={{ cursor: 'pointer', width: '25px', height: '25px' }} 
        />
      </div>
    </div>
  );
};

const VesselList = ({ period }) => {
  const [vessels, setVessels] = useState([]);
  const [error, setError] = useState('');
  const [expandedImage, setExpandedImage] = useState(null);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const params = {
      Bucket: 'git-dash-bucket',
      Key: `at_risk_vessels_${period.toLowerCase()}.json`
    };

    s3.getObject(params, (err, data) => {
      if (err) {
        console.error('Error fetching vessel data:', err);
        setError('Error fetching at-risk vessel list.');
      } else {
        try {
          const parsedData = JSON.parse(data.Body.toString());
          setVessels(parsedData);
        } catch (error) {
          console.error('Error parsing vessel data:', error);
          setError('Error parsing at-risk vessel list.');
        }
      }
    });
  }, [period]);

  const handleClick = (imo, vesselName) => {
    const params = {
      Bucket: 'git-dash-bucket',
      Key: `${imo}-bfp.png`
    };

    if (expandedImage === null || selectedVessel !== vesselName) {
      s3.getObject(params, (err, data) => {
        if (err) {
          console.warn('Error fetching image:', err);
          setSelectedVessel(vesselName);
        } else {
          const blob = new Blob([data.Body], { type: data.ContentType });
          const imageObjectURL = URL.createObjectURL(blob);
          setExpandedImage(imageObjectURL);
          setSelectedVessel(vesselName);
        }
      });
    } else {
      setExpandedImage(null);
      setSelectedVessel(null);
    }
  };

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!vessels) {
    return <div className="vessel-list">Loading vessel list...</div>;
  }

  return (
    <ul className="vessel-list">
      {vessels.map((vessel, index) => (
        <li key={vessel.id || vessel.name} className="vessel-list-item">
          <div onClick={() => handleClick(vessel.imo, vessel.name)} style={{ cursor: 'pointer' }}>
            <span>{`${index + 1}. ${vessel.name || vessel.id}`}</span>
            {expandedImage !== null && selectedVessel === vessel.name && (
              <img
                src={expandedImage}
                alt={`${vessel.name || vessel.id} Image`}
                className="expanded-vessel-image"
                style={{ width: '585px', height: '300px', marginLeft: '10px' }}
              />
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default BiofoulingMonitoringPage;
